import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import {
  ActiveCartService,
  AuthRedirectService,
  AuthService,
  B2BUser,
  B2BUserRole,
  GlobalMessageService,
  GlobalMessageType,
  SemanticPathService,
  StatePersistenceService,
  StorageSyncType,
  User,
} from 'haba-core';
import { CheckoutConfigService } from 'haba-featurelibs/checkout/components';
import { UserAccountFacade } from 'haba-featurelibs/user/account/root';
import { UserSignUp } from 'haba-featurelibs/user/profile/root';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { SessionStorageKeys } from 'src/app/@enums/storage-keys';
import { Urls } from 'src/app/@enums/urls';

@Injectable({
  providedIn: 'root',
})
export class HabaCheckoutAuthGuard {
  private userCredits: UserSignUp;
  constructor(
    protected authService: AuthService,
    protected authRedirectService: AuthRedirectService,
    protected checkoutConfigService: CheckoutConfigService,
    protected activeCartService: ActiveCartService,
    protected semanticPathService: SemanticPathService,
    protected router: Router,
    protected userService: UserAccountFacade,
    protected globalMessageService: GlobalMessageService,
    protected statePersistenceService: StatePersistenceService,
    @Inject(PLATFORM_ID) protected platform_id
  ) {}

  canActivate(): Observable<boolean | UrlTree> {
    if (isPlatformBrowser(this.platform_id)) {
      // CSR
      this.userCredits = this.statePersistenceService.readStateFromStorage({
        key: SessionStorageKeys.USER_SIGN_UP_CREDENTIALS,
        storageType: StorageSyncType.SESSION_STORAGE,
      });

      return combineLatest([
        this.authService.isUserLoggedIn(),
        this.activeCartService.getAssignedUser(),
        this.userService.get(),
        this.activeCartService.isStable(),
      ]).pipe(
        filter(([, , _user, isStable]) => isStable),
        // if the user is authenticated and we have their data, OR if the user is anonymous
        filter(([isLoggedIn, , user]) => (!!user && isLoggedIn) || !isLoggedIn),
        map(([isLoggedIn, cartUser, user]) => {
          if (this.userCredits?.password) return true;

          if (!isLoggedIn) {
            return this.handleAnonymousUser(cartUser);
          } else if (user && 'roles' in user) {
            return this.handleUserRole(user);
          }
          return isLoggedIn;
        })
      );
    } else {
      // SSR
      return of(true);
    }
  }

  protected handleAnonymousUser(cartUser?: User): boolean | UrlTree {
    if (this.activeCartService.isGuestCart()) {
      return !!cartUser;
    }
    this.authRedirectService.saveCurrentNavigationUrl();
    if (this.checkoutConfigService.isGuestCheckout()) {
      return this.router.createUrlTree([this.semanticPathService.get(Urls.CHECKOUT_LOGIN)], {
        queryParams: { forced: true },
      });
    } else {
      return this.router.parseUrl(this.semanticPathService.get(Urls.CHECKOUT_LOGIN));
    }
  }

  protected handleUserRole(user: User): boolean | UrlTree {
    const roles = (<B2BUser>user)?.roles;
    if (roles?.includes(B2BUserRole.CUSTOMER)) {
      return true;
    }
    this.globalMessageService.add(
      { key: 'checkout.invalid.accountType' },
      GlobalMessageType.MSG_TYPE_WARNING
    );
    return this.router.parseUrl(this.semanticPathService.get('home'));
  }
}
